import { CheckIcon, HourglassIcon, MinusIcon, XIcon, type Icon } from '@phosphor-icons/react';

import { IncomingDocumentRejectionReason, OrderDocumentType, OrderDocumentValidationStatus } from 'src/types/enums';

import { PillColor } from '../../../components/pill/Pill';
import { TranslationKey } from '../../../translation/types';

/** Human label (translation key) for each order document type. */
export const OrderDocumentTypeToTranslationKey: Record<OrderDocumentType, TranslationKey> = {
  [OrderDocumentType.Cmr]: 'orderDocumentType.cmr',
  [OrderDocumentType.DeliveryNote]: 'orderDocumentType.deliveryNote',
  [OrderDocumentType.WeighSlip]: 'orderDocumentType.weighSlip',
  [OrderDocumentType.PalletNote]: 'orderDocumentType.palletNote',
  [OrderDocumentType.AnnexII]: 'orderDocumentType.annexII',
  [OrderDocumentType.T1Document]: 'orderDocumentType.t1Document',
  [OrderDocumentType.AnnexVII]: 'orderDocumentType.annexVII',
  [OrderDocumentType.SaleInvoice]: 'orderDocumentType.saleInvoice',
  [OrderDocumentType.TransportOrder]: 'orderDocumentType.transportOrder',
  [OrderDocumentType.OrderConfirmation]: 'orderDocumentType.orderConfirmation',
  [OrderDocumentType.SustainabilityReport]: 'orderDocumentType.sustainabilityReport',
  [OrderDocumentType.CustomerAttachment]: 'orderDocumentType.customerAttachment',
  [OrderDocumentType.Planning]: 'orderDocumentType.planning',
  [OrderDocumentType.Other]: 'orderDocumentType.other',
};

export const OrderDocumentValidationStatusToTranslationKey: Record<OrderDocumentValidationStatus, TranslationKey> = {
  [OrderDocumentValidationStatus.NoDocuments]: 'carrierPortal.orders.documentValidationNoDocuments',
  [OrderDocumentValidationStatus.Approved]: 'carrierPortal.orders.documentValidationApproved',
  [OrderDocumentValidationStatus.Pending]: 'carrierPortal.orders.documentValidationPending',
  [OrderDocumentValidationStatus.Rejected]: 'carrierPortal.orders.documentValidationRejected',
};

export const OrderDocumentValidationStatusToColor: Record<OrderDocumentValidationStatus, PillColor> = {
  [OrderDocumentValidationStatus.NoDocuments]: 'neutral',
  [OrderDocumentValidationStatus.Approved]: 'positive',
  [OrderDocumentValidationStatus.Pending]: 'primary',
  [OrderDocumentValidationStatus.Rejected]: 'negative',
};

/** Per-required-document state as returned by the carrier portal endpoints. */
export type RequiredDocumentStatus = 'satisfied' | 'pending' | 'rejected' | 'missing';

export interface RequiredDocument {
  type: OrderDocumentType;
  status: RequiredDocumentStatus;
  /** Only set on rejected documents that carry a reason. */
  rejectionReasons?: IncomingDocumentRejectionReason[];
  /** Only set on an outstanding CMR of an order with several unload places: one CMR per place. */
  count?: { received: number; required: number };
}

/**
 * Standalone badge labels. Distinct from the `requiredDoc*` keys, which are
 * lowercase sentence fragments the banner appends to a document name
 * ("CMR — ontbreekt").
 */
export const RequiredDocumentStatusToTranslationKey: Record<RequiredDocumentStatus, TranslationKey> = {
  satisfied: 'carrierPortal.orders.requiredDocStatusReceived',
  pending: 'carrierPortal.orders.requiredDocStatusInReview',
  rejected: 'carrierPortal.orders.requiredDocStatusRejected',
  missing: 'carrierPortal.orders.requiredDocStatusMissing',
};

export const RequiredDocumentStatusToColor: Record<RequiredDocumentStatus, PillColor> = {
  satisfied: 'positive',
  pending: 'primary',
  rejected: 'negative',
  missing: 'neutral',
};

/** Glyph carrying the status of a required-document badge without reading it. */
export const RequiredDocumentStatusToIcon: Record<RequiredDocumentStatus, Icon> = {
  satisfied: CheckIcon,
  pending: HourglassIcon,
  rejected: XIcon,
  missing: MinusIcon,
};

/**
 * Carrier-facing wording for a refusal. The planner-facing map in
 * `incoming-document/utils/status` is Dutch-only; the portal is translated, so
 * it gets its own keys.
 */
export const IncomingDocumentRejectionReasonToTranslationKey: Record<IncomingDocumentRejectionReason, TranslationKey> =
  {
    [IncomingDocumentRejectionReason.LowQuality]: 'carrierPortal.orders.docRejectionLowQuality',
    [IncomingDocumentRejectionReason.MissingSignature]: 'carrierPortal.orders.docRejectionMissingSignature',
    [IncomingDocumentRejectionReason.NoOrderReference]: 'carrierPortal.orders.docRejectionNoOrderReference',
    [IncomingDocumentRejectionReason.NotNeutral]: 'carrierPortal.orders.docRejectionNotNeutral',
    [IncomingDocumentRejectionReason.WrongCustomer]: 'carrierPortal.orders.docRejectionWrongCustomer',
    [IncomingDocumentRejectionReason.WrongDeliveryAddress]: 'carrierPortal.orders.docRejectionWrongDeliveryAddress',
    [IncomingDocumentRejectionReason.WrongDocumentType]: 'carrierPortal.orders.docRejectionWrongDocumentType',
    [IncomingDocumentRejectionReason.WrongOrgOnDocument]: 'carrierPortal.orders.docRejectionWrongOrgOnDocument',
  };

/**
 * Document types a carrier can upload itself. Anything else on a customer's
 * required list (e.g. SaleInvoice) is produced internally, so it never shows up
 * as an upload option.
 */
export const CARRIER_UPLOADABLE_DOCUMENT_TYPES: OrderDocumentType[] = [
  OrderDocumentType.Cmr,
  OrderDocumentType.DeliveryNote,
  OrderDocumentType.WeighSlip,
  OrderDocumentType.PalletNote,
  OrderDocumentType.AnnexII,
  OrderDocumentType.T1Document,
  OrderDocumentType.AnnexVII,
];

export function isCarrierUploadableDocumentType(type: OrderDocumentType): boolean {
  return CARRIER_UPLOADABLE_DOCUMENT_TYPES.includes(type);
}
